import React, { useCallback, useEffect, useRef, useState } from 'react';
import { resolveSkinAssetUrl } from '../../../data/profileSkins';

export const BOX_W = 420;
export const HEART_BOX = 60;
export const DEFAULT_HEADER_BG = '/img/grey_tile.png';
export const DEFAULT_LUV_COLOUR = '#e72354';
export const RAINBOW = ['#f4a8b0', '#f7c39b', '#f7e3a6', '#bce3b5', '#aed6ef', '#c5b6e8', '#e9b4d3'];

// Mirrors EditableBox: header and body assets both anchor to the whole box,
// only heart assets are handled separately (and those are always centred).
export const isBoxAsset = (a) => {
    const p = a?.placement;
    if (!p) return true;
    if (Array.isArray(p)) return p.some(pl => pl === 'header' || pl === 'body');
    return p === 'header' || p === 'body';
};

export const isHeartAsset = (a) => {
    const p = a?.placement;
    if (Array.isArray(p)) return p.includes('heart');
    return typeof p === 'string' && p.includes('heart');
};

const hexToRgb = (hex) => {
    const h = String(hex || '').trim();
    if (!/^#[0-9A-Fa-f]{6}$/.test(h)) return { r: 231, g: 35, b: 84 };
    return { r: parseInt(h.slice(1, 3), 16), g: parseInt(h.slice(3, 5), 16), b: parseInt(h.slice(5, 7), 16) };
};

export function assetStyle(asset) {
    const { anchor, offsets = {} } = asset;
    const style = { position: 'absolute', imageRendering: 'pixelated', maxWidth: 'none' };

    if (anchor === 'top-right') { style.top = `${offsets.top ?? 0}px`; style.right = `${offsets.right ?? 0}px`; }
    else if (anchor === 'top-left') { style.top = `${offsets.top ?? 0}px`; style.left = `${offsets.left ?? 0}px`; }
    else if (anchor === 'top-center') { style.top = `${offsets.top ?? 0}px`; style.left = '50%'; style.transform = `translateX(calc(-50% + ${offsets.x ?? 0}px))`; }
    else if (anchor === 'bottom-right') { style.bottom = `${offsets.bottom ?? 0}px`; style.right = `${offsets.right ?? 0}px`; }
    else if (anchor === 'bottom-left') { style.bottom = `${offsets.bottom ?? 0}px`; style.left = `${offsets.left ?? 0}px`; }
    else if (anchor === 'bottom-center') { style.bottom = `${offsets.bottom ?? 0}px`; style.left = '50%'; style.transform = `translateX(calc(-50% + ${offsets.x ?? 0}px))`; }
    else { style.top = `${offsets.top ?? 0}px`; style.left = `${offsets.left ?? 0}px`; }

    return style;
}

// Offsets are anchor-relative, so switching anchor has to go through an
// absolute position or the asset jumps to the other side of the box.
export function convertOffsets(offsets = {}, fromAnchor, toAnchor, boxSize, imgSize) {
    const zw = boxSize?.w || BOX_W;
    const zh = boxSize?.h || 0;
    const iw = imgSize?.w || 0;
    const ih = imgSize?.h || 0;

    const from = fromAnchor || 'top-left';
    const to = toAnchor || 'top-left';

    let x;
    if (from.endsWith('-left')) x = offsets.left ?? 0;
    else if (from.endsWith('-right')) x = zw - (offsets.right ?? 0) - iw;
    else x = (zw - iw) / 2 + (offsets.x ?? 0);

    const y = from.startsWith('top') ? (offsets.top ?? 0) : zh - (offsets.bottom ?? 0) - ih;

    const next = {};
    if (to.endsWith('-left')) next.left = Math.round(x);
    else if (to.endsWith('-right')) next.right = Math.round(zw - x - iw);
    else next.x = Math.round(x - (zw - iw) / 2);

    if (to.startsWith('top')) next.top = Math.round(y);
    else next.bottom = Math.round(zh - y - ih);

    return next;
}

function DraggableAsset({ asset, index, selected, onSelect, onMove, onSize }) {
    const dragRef = useRef(null);

    const handlePointerDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect(index);

        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            offsets: { ...(asset.offsets || {}) },
            anchor: asset.anchor || 'top-left'
        };
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e) => {
        const drag = dragRef.current;
        if (!drag) return;

        const dx = Math.round(e.clientX - drag.startX);
        const dy = Math.round(e.clientY - drag.startY);
        const { anchor, offsets } = drag;
        const next = { ...offsets };

        if (anchor.endsWith('-left')) next.left = (offsets.left ?? 0) + dx;
        else if (anchor.endsWith('-right')) next.right = (offsets.right ?? 0) - dx;
        else next.x = (offsets.x ?? 0) + dx;

        if (anchor.startsWith('top')) next.top = (offsets.top ?? 0) + dy;
        else next.bottom = (offsets.bottom ?? 0) - dy;

        onMove(index, next);
    };

    const endDrag = (e) => {
        if (!dragRef.current) return;
        dragRef.current = null;
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    };

    return (
        <img
            src={resolveSkinAssetUrl(asset.image)}
            alt=""
            draggable={false}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onLoad={(e) => onSize(index, { w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
            onError={(e) => { e.currentTarget.style.opacity = '0.15'; }}
            style={{
                ...assetStyle(asset),
                zIndex: selected ? 40 : 5,
                cursor: 'move',
                touchAction: 'none',
                outline: selected ? '2px solid #ffcc66' : 'none',
                outlineOffset: 1
            }}
        />
    );
}

function LuvHeartMock({ skin, selectedIndex, onSelect, luvColour }) {
    const assets = skin.assets || [];
    const { r, g, b } = hexToRgb(luvColour || DEFAULT_LUV_COLOUR);
    const filterId = 'skinEditorLuvTint';

    const entries = assets
        .map((asset, index) => ({ asset, index }))
        .filter(({ asset }) => isHeartAsset(asset));

    const behind = entries.filter(({ asset }) => asset.heartDepth === 'behind');
    const infront = entries.filter(({ asset }) => asset.heartDepth !== 'behind');

    const layerStyle = (index, z) => ({
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        imageRendering: 'pixelated',
        maxWidth: 'none',
        zIndex: selectedIndex === index ? 40 : z,
        cursor: 'pointer',
        outline: selectedIndex === index ? '2px solid #ffcc66' : 'none',
        outlineOffset: 1
    });

    return (
        <div className="border border-gray-300 bg-offwhite" style={{ width: BOX_W }}>
            <div className="bg-maroon px-4 py-2">
                <span className="font-['Jersey_10'] text-[18px] uppercase tracking-[1px] text-white">
                    &#10084;&#65039; Luv
                </span>
            </div>

            <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
                <filter id={filterId} colorInterpolationFilters="sRGB">
                    <feColorMatrix
                        type="matrix"
                        values={`${(r / 255).toFixed(6)} 0 0 0 0  0 ${(g / 255).toFixed(6)} 0 0 0  0 0 ${(b / 255).toFixed(6)} 0 0  0 0 0 1 0`}
                    />
                </filter>
            </svg>

            <div className="flex items-center justify-center gap-3 px-4 pb-2 pt-4">
                <div className="flex flex-col items-center gap-0.5">
                    <div className="font-['Jersey_10'] text-5xl leading-none text-coal tabular-nums">1,337</div>
                    <div className="font-['Jersey_10'] text-lg uppercase leading-none tracking-widest text-coal/60">Total Luv</div>
                </div>

                <div className="relative ml-2 flex items-center justify-center" style={{ width: HEART_BOX, height: HEART_BOX }}>
                    {behind.map(({ asset, index }) => (
                        <img
                            key={index}
                            src={resolveSkinAssetUrl(asset.image)}
                            alt=""
                            draggable={false}
                            onClick={(e) => { e.stopPropagation(); onSelect(index); }}
                            onError={(e) => { e.currentTarget.style.opacity = '0.15'; }}
                            style={layerStyle(index, 1)}
                        />
                    ))}

                    <img
                        src="/img/luv_heart_lg.png"
                        alt=""
                        draggable={false}
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%) scale(2)',
                            display: 'block',
                            filter: `url(#${filterId}) drop-shadow(0 2px 6px rgba(0,0,0,0.25))`,
                            imageRendering: 'pixelated',
                            zIndex: 2
                        }}
                    />

                    {infront.length > 0 ? (
                        infront.map(({ asset, index }) => (
                            <img
                                key={index}
                                src={resolveSkinAssetUrl(asset.image)}
                                alt=""
                                draggable={false}
                                onClick={(e) => { e.stopPropagation(); onSelect(index); }}
                                onError={(e) => { e.currentTarget.style.opacity = '0.15'; }}
                                style={layerStyle(index, 3)}
                            />
                        ))
                    ) : (
                        <img
                            src="/img/luv_heart_lg_sparkle.png"
                            alt=""
                            draggable={false}
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%) scale(2)',
                                imageRendering: 'pixelated',
                                zIndex: 3
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

function SkinCanvas({
    skin,
    selectedIndex,
    onSelect,
    onMoveAsset,
    onAssetSize,
    onDropFile,
    onBoxSize,
    clip = false,
    headerColour = '#5A2929',
    luvColour = DEFAULT_LUV_COLOUR,
    wallpaper = ''
}) {
    const [dragOver, setDragOver] = useState(false);
    const boxRef = useRef(null);

    const assets = skin.assets || [];
    const skinBg = skin.backgroundImage || '';
    const headerBg = resolveSkinAssetUrl(skinBg) || DEFAULT_HEADER_BG;
    const isOverlay = skin.backgroundMode === 'overlay';
    const vOffset = Number(skin.verticalOffset) || 0;
    const hasVOffset = !!vOffset && !!skinBg;
    const grad = skin.borderAnimGradient || skin.borderGradient;

    // The drag maths needs the real box height, which the header tile decides.
    useEffect(() => {
        const el = boxRef.current;
        if (!el || !onBoxSize) return;

        const report = () => onBoxSize({ w: el.offsetWidth, h: el.offsetHeight });
        report();

        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(report);
        ro.observe(el);
        return () => ro.disconnect();
    }, [onBoxSize]);

    const radius = skin.borderRadius || '0px';

    const boxStyle = {
        width: BOX_W,
        borderRadius: radius,
        position: 'relative',
        backgroundColor: '#F2F2F2',
        zIndex: 1,
        overflow: clip ? 'hidden' : 'visible'
    };

    if (grad) {
        const colors = grad === 'rainbow' ? RAINBOW : [grad, grad];
        boxStyle.border = '2px solid transparent';
        boxStyle.backgroundImage = `linear-gradient(#F2F2F2, #F2F2F2), linear-gradient(90deg, ${colors.join(', ')})`;
        boxStyle.backgroundOrigin = 'border-box';
        boxStyle.backgroundClip = 'padding-box, border-box';
    } else {
        boxStyle.border = `1px solid ${skin.borderInner || '#d1d5db'}`;
        const rings = ['0 3px 0 0 rgba(0,0,0,0.5)'];
        if (skin.borderMiddle) rings.push(`0 0 0 1px ${skin.borderMiddle}`);
        if (skin.borderOuter) rings.push(`0 0 0 2px ${skin.borderOuter}`);
        boxStyle.boxShadow = rings.join(', ');
    }

    // Matches ContentBox: tiled header art at natural size, tinted by the
    // header colour (multiply for cover skins, straight tint for overlays).
    const headerStyle = {
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto',
        backgroundPosition: 'left top',
        overflow: 'hidden',
        backgroundColor: skinBg ? headerColour : `${headerColour}80`,
        ...(skinBg
            ? (isOverlay ? {} : { backgroundBlendMode: 'multiply' })
            : { backgroundBlendMode: 'screen' }),
        borderTopLeftRadius: radius,
        borderTopRightRadius: radius,
        ...(hasVOffset ? { position: 'relative' } : { backgroundImage: `url(${headerBg})` })
    };

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer?.files?.[0];
        if (file && onDropFile) onDropFile(file);
    }, [onDropFile]);

    const stageStyle = {
        minHeight: 520,
        backgroundColor: '#3a3a3a',
        outline: dragOver ? '3px dashed #ffcc66' : 'none',
        outlineOffset: -6
    };

    if (wallpaper) {
        stageStyle.backgroundImage = `url(${resolveSkinAssetUrl(wallpaper)})`;
        stageStyle.backgroundRepeat = 'repeat';
        stageStyle.backgroundSize = 'auto';
    } else {
        stageStyle.backgroundImage =
            'linear-gradient(45deg, rgba(255,255,255,0.04) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.04) 75%),' +
            'linear-gradient(45deg, rgba(255,255,255,0.04) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.04) 75%)';
        stageStyle.backgroundSize = '20px 20px';
        stageStyle.backgroundPosition = '0 0, 10px 10px';
    }

    return (
        <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onPointerDown={() => onSelect(null)}
            className="relative flex w-full flex-col items-center gap-6 overflow-auto rounded p-14"
            style={stageStyle}
        >
            {/* Box wrapper — assets sit on this, exactly as EditableBox does it */}
            <div className="relative isolate" style={{ width: BOX_W }}>
                <div ref={boxRef} style={boxStyle}>
                    <div className="flex items-center gap-2 px-4 py-2" style={headerStyle}>
                        {hasVOffset && (
                            <div aria-hidden="true" className="absolute inset-0 flex items-stretch overflow-hidden" style={{ zIndex: 0 }}>
                                {Array.from({ length: 30 }).map((_, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            flexShrink: 0,
                                            backgroundImage: `url(${headerBg})`,
                                            backgroundRepeat: 'repeat-y',
                                            backgroundSize: 'auto',
                                            backgroundPositionY: `${i * vOffset}px`
                                        }}
                                    >
                                        <img src={headerBg} alt="" draggable={false} style={{ visibility: 'hidden', display: 'block' }} />
                                    </div>
                                ))}
                            </div>
                        )}
                        <span
                            className="relative font-['Jersey_10'] text-[18px] uppercase tracking-[1px] text-white"
                            style={{ textShadow: '-1px 0 0 #000, 1px 0 0 #000, 0 -1px 0 #000, 0 1px 0 #000' }}
                        >
                            {skin.name || 'Skin preview'}
                        </span>
                    </div>

                    <div
                        className="px-4 py-4"
                        style={{ minHeight: 190, borderBottomLeftRadius: radius, borderBottomRightRadius: radius }}
                    >
                        <p className="text-[13px] text-gray-700">
                            This is a profile box. Drag any asset to place it around the whole box &mdash;
                            header and body assets both anchor here.
                        </p>
                    </div>
                </div>

                {assets
                    .map((asset, index) => ({ asset, index }))
                    .filter(({ asset }) => isBoxAsset(asset))
                    .map(({ asset, index }) => (
                        <DraggableAsset
                            key={index}
                            asset={asset}
                            index={index}
                            selected={selectedIndex === index}
                            onSelect={onSelect}
                            onMove={onMoveAsset}
                            onSize={onAssetSize}
                        />
                    ))}
            </div>

            <LuvHeartMock
                skin={skin}
                selectedIndex={selectedIndex}
                onSelect={onSelect}
                luvColour={luvColour}
            />

            {dragOver && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <span className="rounded bg-black/70 px-4 py-2 font-['Jersey_10'] text-[20px] uppercase tracking-[1px] text-gold">
                        Drop to upload
                    </span>
                </div>
            )}
        </div>
    );
}

export default SkinCanvas;
